/**
 * 错题本API函数
 * 提供错题本相关功能，如获取错题、错题统计等
 */
import { supabase } from '../supabaseClient';

/**
 * 获取用户的错题列表
 * @param {string} userId - 用户ID
 * @param {Object} options - 查询选项
 * @returns {Promise<Array>} 错题列表
 */
export const getUserMistakes = async (userId, options = {}) => {
  try {
    if (!userId) throw new Error('用户ID不能为空');
    
    // 直接从mistakes表获取错题
    const query = supabase
      .from('mistakes')
      .select('*')
      .eq('user_id', userId);
    
    if (options.examId) {
      query.eq('question->>examid', Number(options.examId));
    }
    
    if (options.limit) {
      query.limit(Number(options.limit));
    }
    
    // 按创建时间倒序
    query.order('created_at', { ascending: false });
    
    const { data, error } = await query;
    
    if (error) throw error;
    
    return data || [];
  } catch (error) {
    console.error('获取错题列表出错:', error);
    return [];
  }
};

/**
 * 获取错题统计数据
 * @param {string} userId - 用户ID
 * @returns {Promise<Object>} 错题统计数据
 */
export const getMistakesStats = async (userId) => {
  try {
    if (!userId) throw new Error('用户ID不能为空');
    
    // 从mistakes表获取错题
    const { data, error } = await supabase
      .from('mistakes')
      .select('*')
      .eq('user_id', userId);
    
    if (error) throw error;
    
    // 手动统计错题数据
    const examStats = {};
    let totalMistakes = 0;
    
    (data || []).forEach(mistake => {
      const question = mistake.question || {};
      const examId = question.examid;
      const subjectId = question.subjectId;
      
      if (!examId) return;
      
      if (!examStats[examId]) {
        examStats[examId] = {
          id: examId,
          name: question.examName || `考试${examId}`,
          totalMistakes: 0,
          subjects: []
        };
      }
      
      // 查找科目
      let subject = examStats[examId].subjects.find(s => s.id === subjectId);
      if (!subject) {
        subject = {
          id: subjectId,
          name: question.subjectName || `科目${subjectId}`,
          mistakeCount: 0
        };
        examStats[examId].subjects.push(subject);
      }
      
      subject.mistakeCount++;
      examStats[examId].totalMistakes++;
      totalMistakes++;
    });
    
    return {
      totalMistakes,
      examStats: Object.values(examStats)
    };
  } catch (error) {
    console.error('获取错题统计数据出错:', error);
    return { totalMistakes: 0, examStats: [] };
  }
};

/**
 * 获取错题详情
 * @param {string} userId - 用户ID
 * @param {number} questionId - 题目ID
 * @returns {Promise<Object|null>} 错题详情
 */
export const getMistakeDetail = async (userId, questionId) => {
  try {
    if (!userId || !questionId) throw new Error('用户ID和题目ID不能为空');
    
    // 直接从mistakes表获取错题
    const { data, error } = await supabase
      .from('mistakes')
      .select('*')
      .eq('user_id', userId)
      .eq('_id', questionId)
      .single();
    
    if (error) throw error;
    
    return data;
  } catch (error) {
    console.error('获取错题详情出错:', error);
    return null;
  }
};

/**
 * 添加题目到错题本
 * @param {string} userId - 用户ID
 * @param {Object} question - 题目信息
 * @param {string} userAnswer - 用户答案
 * @returns {Promise<Object>} 添加结果
 */
export const addMistake = async (userId, question, userAnswer) => {
  try {
    if (!userId) throw new Error('用户ID不能为空');
    if (!question) throw new Error('题目信息不能为空');
    
    // 检查是否已经存在相同的错题
    const questionId = question._id || question.id;
    const examId = question.examid;
    
    if (questionId) {
      const { data: existingMistakes } = await supabase
        .from('mistakes')
        .select('*')
        .eq('user_id', userId)
        .eq('question->>title', question.title);
      
      // 如果已经存在相同的错题，不再添加
      if (existingMistakes && existingMistakes.length > 0) {
        console.log('错题已存在，不重复添加');
        return { success: true, data: existingMistakes[0], duplicate: true };
      }
    }
    
    // 生成唯一ID
    const mistakeId = `${Date.now()}_${Math.random().toString().substring(2, 10)}`;
    
    // 构建错题数据，确保包含考试ID和科目ID
    const mistakeData = {
      _id: mistakeId,
      user_id: userId,
      question: {
        title: question.title,
        examid: examId || question.examid,
        subjectId: question.subjectId,
        examName: question.examName,
        subjectName: question.subjectName,
        comments: question.comments || '',
        userAnswer: userAnswer
      },
      options: question.options,
      ordernum: question.ordernum || 0,
      created_at: new Date().toISOString()
    };
    
    // 插入错题
    const { data, error } = await supabase
      .from('mistakes')
      .insert([mistakeData]);
      
    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    console.error('添加错题出错:', error);
    return { success: false, error: error.message };
  }
};

/**
 * 删除错题
 * @param {string} userId - 用户ID
 * @param {string} mistakeId - 错题ID
 * @returns {Promise<Object>} 删除结果
 */
export const deleteMistake = async (userId, mistakeId) => {
  try {
    if (!userId) throw new Error('用户ID不能为空');
    if (!mistakeId) throw new Error('错题ID不能为空');
    
    const { error } = await supabase
      .from('mistakes')
      .delete()
      .eq('_id', mistakeId)
      .eq('user_id', userId);
      
    if (error) throw error;
    
    return { success: true };
  } catch (error) {
    console.error('删除错题出错:', error);
    return { success: false, error: error.message };
  }
};

/**
 * 清空用户错题本
 * @param {string} userId - 用户ID
 * @returns {Promise<Object>} 清空结果
 */
export const clearAllMistakes = async (userId) => {
  try {
    if (!userId) throw new Error('用户ID不能为空');
    
    const { error } = await supabase
      .from('mistakes')
      .delete()
      .eq('user_id', userId);
      
    if (error) throw error;
    
    return { success: true };
  } catch (error) {
    console.error('清空错题本出错:', error);
    return { success: false, error: error.message };
  }
};

/**
 * 批量添加错题
 * @param {string} userId - 用户ID
 * @param {Array} wrongQuestions - 错题数组
 * @param {Object} answers - 用户答案对象，键为题目ID，值为用户答案
 * @returns {Promise<Object>} 添加结果
 */
export const addMistakesBatch = async (userId, wrongQuestions, answers) => {
  try {
    if (!userId) throw new Error('用户ID不能为空');
    if (!wrongQuestions || !Array.isArray(wrongQuestions) || wrongQuestions.length === 0) {
      return { success: true, savedCount: 0 };
    }
    
    // 先检查是否已存在相同的错题
    const titles = wrongQuestions.map(q => q.title);
    
    // 查询已存在的错题
    const { data: existingMistakes } = await supabase
      .from('mistakes')
      .select('question')
      .eq('user_id', userId);
    
    // 提取已存在错题的标题，用于过滤
    const existingTitles = existingMistakes 
      ? existingMistakes.map(m => m.question?.title).filter(Boolean) 
      : [];
    
    // 过滤掉已经存在的错题
    const newWrongQuestions = wrongQuestions.filter(q => 
      !existingTitles.includes(q.title)
    );
    
    if (newWrongQuestions.length === 0) {
      console.log('所有错题都已存在，不需要重复添加');
      return { success: true, savedCount: 0 };
    }
    
    // 准备批量插入数据
    const mistakesData = newWrongQuestions.map(question => {
      // 获取题目ID和用户答案
      const questionId = question._id || question.id;
      const userAnswer = answers[questionId] || '';
      
      // 生成唯一ID
      const mistakeId = `${Date.now()}_${Math.random().toString().substring(2, 10)}`;
      
      return {
        _id: mistakeId,
        user_id: userId,
        question: {
          title: question.title,
          examid: question.examid,
          subjectId: question.subjectId,
          examName: question.examName,
          subjectName: question.subjectName,
          comments: question.comments || '',
          userAnswer: userAnswer
        },
        options: question.options,
        ordernum: question.ordernum || 0,
        created_at: new Date().toISOString()
      };
    });
    
    // 批量插入错题
    const { data, error } = await supabase
      .from('mistakes')
      .insert(mistakesData);
      
    if (error) throw error;
    
    return { 
      success: true, 
      savedCount: mistakesData.length,
      data 
    };
  } catch (error) {
    console.error('批量添加错题出错:', error);
    return { success: false, savedCount: 0, error: error.message };
  }
}; 